// Project building blocks (FD §13): the repositories/workflow summary, the policy
// editor (raw YAML, FD §13.2) and the project knowledge browser. They are
// shown on the project's settings and knowledge pages (routes/project/*);
// the old /projects page is a redirect (router.tsx).

import { ClayList } from '../components/goblin-art'
import {
  KnowledgeBrowser,
  type KbLocation,
} from '../components/knowledge/knowledge-browser'
import { Link } from '@tanstack/react-router'
import { formatKbSpace } from '@goblic/shared'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project } from '@goblic/shared'
import { projectsApi } from '../lib/api-client'
import { ErrorNote, LoadingRows, Notice, Panel } from '../components/ui'
import { humanize } from '../lib/format'

/** The project's own knowledge space, with the same browser as /knowledge. */
export function ProjectKnowledgeTab({ project }: { project: Project }) {
  const space = formatKbSpace({ kind: 'project', projectId: project.id })
  const [location, setLocation] = useState<KbLocation>({})
  return (
    <KnowledgeBrowser
      projectId={project.id}
      location={location}
      onNavigate={setLocation}
      toolbarExtra={
        location.space && location.path ? (
          <Link
            to="/knowledge/$space/$"
            params={{ space: location.space, _splat: location.path }}
            className="btn-ghost"
          >
            Open in Knowledge
          </Link>
        ) : (
          <Link to="/knowledge/$space" params={{ space }} className="btn-ghost">
            Open in Knowledge
          </Link>
        )
      }
    />
  )
}

export function ProjectOverview({ project }: { project: Project }) {
  const workflow = project.policy?.workflow ?? []
  // How many other projects share each repository (a count, nothing more).
  const links = useQuery({
    queryKey: ['project', project.id, 'repository-links', project.repositories.map((r) => r.url)],
    queryFn: () => projectsApi.repositoryLinks(project.id),
    enabled: project.repositories.length > 0,
  })
  const sharedWith = (url: string): number =>
    links.data?.items.find((item) => item.url === url)?.otherProjectCount ?? 0
  return (
    <div className="space-y-6">
      <Panel title="Repositories" flush>
        {project.repositories.length ? (
          <ul className="divide-y divide-line">
            {project.repositories.map((repo) => (
              <li
                key={repo.id ?? repo.url}
                className="flex items-center justify-between gap-4 px-5 py-3"
              >
                <span className="min-w-0">
                  <span className="block truncate font-mono text-sm text-ink">
                    {repo.url.replace(/^https?:\/\//, '')}
                  </span>
                  {sharedWith(repo.url) > 0 ? (
                    <span className="block text-sm text-ink-3">
                      This repository is also connected to{' '}
                      {sharedWith(repo.url)} other{' '}
                      {sharedWith(repo.url) === 1 ? 'project' : 'projects'}.
                      Events from it reach each of them.
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-sm text-ink-3">
                  {repo.defaultBranch}
                  {repo.isDefault ? ', default' : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-5 text-base text-ink-3">
            No repositories linked.
          </p>
        )}
        {/* TODO: repository/documentation source management (FD §13.1). */}
      </Panel>

      <Panel
        title="Workflow"
        description="The states a task moves through, and which role handles each."
      >
        {workflow.length ? (
          <ol className="space-y-0">
            {workflow.map((state, i) => (
              <li
                key={state.name}
                className="grid grid-cols-[28px_minmax(0,1fr)] gap-3"
              >
                <div className="flex flex-col items-center">
                  <span className="grid h-6 w-6 place-items-center rounded-full border border-line-strong bg-surface text-xs font-semibold text-ink-2">
                    {i + 1}
                  </span>
                  {i < workflow.length - 1 ? (
                    <span className="w-px flex-1 bg-line" />
                  ) : null}
                </div>
                <div className="pb-4">
                  <p className="font-medium text-ink">{humanize(state.name)}</p>
                  <p className="text-sm text-ink-3">
                    {state.agentRole
                      ? `Handled by ${humanize(state.agentRole)}`
                      : 'Handled by a person'}
                    {state.requires.length
                      ? `. Needs ${state.requires.map((r) => humanize(r).toLowerCase()).join(', ')}`
                      : ''}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-base text-ink-3">
            This policy has no workflow states yet. Add them in the Policy tab.
          </p>
        )}
      </Panel>

      {project.policy?.humanApproval?.length ||
      project.policy?.forbidden?.length ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Panel title="Always needs approval">
            <ClayList
              icon="bell"
              items={(project.policy.humanApproval ?? []).map(humanize)}
            />
          </Panel>
          <Panel title="Never allowed">
            <ClayList
              icon="no-entry"
              items={(project.policy.forbidden ?? []).map(humanize)}
            />
          </Panel>
        </div>
      ) : null}
    </div>
  )
}

export function PolicyEditor({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const [yaml, setYaml] = useState<string | null>(null)
  const [validation, setValidation] = useState<{
    valid: boolean
    errors: { path: string; message: string }[]
  } | null>(null)

  const policyQuery = useQuery({
    queryKey: ['project', project.id, 'policy'],
    queryFn: () => projectsApi.getPolicy(project.id),
  })

  const currentYaml = yaml ?? policyQuery.data?.rawYaml ?? ''
  const dirty = yaml != null && yaml !== policyQuery.data?.rawYaml

  const validateMutation = useMutation({
    mutationFn: () => projectsApi.validatePolicy(project.id, currentYaml),
    onSuccess: (result) => setValidation(result),
  })

  const saveMutation = useMutation({
    mutationFn: () => projectsApi.updatePolicy(project.id, currentYaml),
    onSuccess: (record) => {
      setValidation({ valid: true, errors: [] })
      void queryClient.invalidateQueries({
        queryKey: ['project', project.id, 'policy'],
      })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setYaml(record.rawYaml)
    },
    onError: (error: unknown) => {
      const details = (
        error as { details?: { errors?: { path: string; message: string }[] } }
      ).details
      if (details?.errors)
        setValidation({ valid: false, errors: details.errors })
    },
  })

  return (
    <Panel
      title={
        policyQuery.data
          ? `Policy, version ${policyQuery.data.version}`
          : 'Policy'
      }
      description="Saving creates a new version. Runs already in progress keep the version they started with."
      flush
    >
      {policyQuery.isLoading ? (
        <LoadingRows label="Loading policy…" />
      ) : policyQuery.isError ? (
        <ErrorNote error={policyQuery.error} what="the policy" />
      ) : (
        <>
          <textarea
            aria-label="Policy YAML"
            className="block h-[26rem] w-full resize-y border-0 bg-sunken/40 px-5 py-4 font-mono text-sm leading-relaxed text-ink focus:outline-none focus:ring-0"
            value={currentYaml}
            onChange={(event) => {
              setYaml(event.target.value)
              setValidation(null)
            }}
            spellCheck={false}
          />
          <div className="space-y-3 border-t border-line px-5 py-4">
            {validation ? (
              validation.valid ? (
                <Notice tone="success">The policy is valid.</Notice>
              ) : (
                <Notice tone="error">
                  <ul className="space-y-1">
                    {validation.errors.map((error, index) => (
                      <li key={index}>
                        <code className="font-mono text-sm">{error.path}</code>{' '}
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </Notice>
              )
            ) : null}
            <div className="flex items-center justify-end gap-2">
              {dirty ? (
                <span className="mr-auto text-sm text-ink-3">
                  Unsaved changes
                </span>
              ) : null}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => validateMutation.mutate()}
                disabled={validateMutation.isPending}
              >
                Check policy
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !dirty}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save as new version'}
              </button>
            </div>
          </div>
        </>
      )}
    </Panel>
  )
}
